import * as Sentry from '@sentry/react'
import {
  resolveSentryEnvironment,
  scrubSentryEvent,
} from '@order-up/analytics/sentry'
import { installChunkReloadRecovery } from '@order-up/client/vite'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { AnalyticsBoundary } from './components/AnalyticsBoundary'
import { AppShellError } from './components/AppShellError'
import './index.css'
import App from './App.tsx'

// Bridge the build-time Vite env (define'd as the bare `__VITE_ENV__` token in
// vite.config.ts) onto globalThis. `@order-up/common`'s environment detection
// reads `globalThis.__VITE_ENV__`; without this assignment it falls through to
// its Next.js branch and resolves the deployment-protected `.dev` service URLs,
// bouncing onboarded users to the Vercel SSO wall instead of app.letsorderup.com.
// Runs before any service-URL resolution (React render / redirects).
// See packages/common/src/utils/environment.ts (isViteEnvironment / getAppEnv).
if (typeof __VITE_ENV__ !== 'undefined') {
  ;(globalThis as Record<string, unknown>).__VITE_ENV__ ??= __VITE_ENV__
}

// Boot recovery: if a JS module / modulepreload fetch fails on first load
// (common on the cross-origin auth → onboarding handoff after email
// verification), auto-reload once instead of wedging on the loading shell.
// Registered before render so it catches a failed entry/vendor chunk.
// (Incident: Squeaky Wizard onboarding, 2026-06-17.)
installChunkReloadRecovery({
  onRecover: ({ url, reason }) => {
    Sentry.addBreadcrumb({
      category: 'boot',
      level: 'warning',
      message: 'chunk-load recovery reload',
      data: { url, reason },
    })
    Sentry.captureMessage('chunk-load recovery reload', 'warning')
  },
})

// Sentry browser bootstrap (Phase 12 / Initiative #3 PR 8).
// Resolved once and shared by `environment` and `beforeSend` — they disagreed
// before (one read VITE_APP_ENV first, the other VITE_VERCEL_ENV), so an event
// could be labelled `production` by one and judged non-production by the other.
const sentryEnvironment = resolveSentryEnvironment(
  import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined,
  import.meta.env.VITE_APP_ENV as string | undefined,
  import.meta.env.VITE_VERCEL_ENV as string | undefined,
  import.meta.env.MODE,
)

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
if (dsn) {
  Sentry.init({
    dsn,
    environment: sentryEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    sampleRate: parseFloat(
      (import.meta.env.VITE_SENTRY_SAMPLE_RATE as string | undefined) || '1.0',
    ),
    tracesSampleRate: parseFloat(
      (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined)
        || '0.1',
    ),
    initialScope: {
      tags: { app: 'onboarding' },
    },
    sendDefaultPii: false,
    beforeSend: (event) => scrubSentryEvent(event, sentryEnvironment),
  })
} else if (import.meta.env.PROD) {
  // Loud breadcrumb: a production build with no DSN means client-side errors
  // — including a white-screen crash like the 2026-05-30 duplicate-React
  // incident — are NOT captured. Set VITE_SENTRY_DSN in the onboarding Vercel
  // project so the error monitoring + alerting below actually has a feed.
  console.warn(
    '[onboarding] VITE_SENTRY_DSN is not set — client error monitoring is DISABLED for this build.',
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      Root error boundary ABOVE AnalyticsBoundary so a crash in the very first
      provider (which is exactly where the duplicate-React null-dispatcher blew
      up) renders a graceful error card instead of a blank page, AND reports to
      Sentry (when a DSN is configured) via the global hub init'd above.
      Defense-in-depth — the resolve.dedupe + pnpm.overrides fix prevents the
      crash itself; this makes any future first-paint crash visible + reported
      rather than a silent white screen.
    */}
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <AppShellError
          title="Something went wrong"
          description={
            <p>
              We hit an unexpected error while loading onboarding. Please try
              again — if it keeps happening, contact support.
            </p>
          }
          onRetry={() => {
            resetError()
            window.location.reload()
          }}
        />
      )}
    >
      <AnalyticsBoundary>
        <App />
      </AnalyticsBoundary>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
